// frontend/src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DepartmentProvider } from './context/DepartmentContext';
import { rolesForRoute } from './content/modules';
import Layout from './components/Layout';
import Login       from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard   from './pages/Dashboard';
import Reviews     from './pages/Reviews';
import ReviewForm  from './pages/ReviewForm';
import OKRs        from './pages/OKRs';
import Checkins    from './pages/Checkins';
import Feedback    from './pages/Feedback';
import Career      from './pages/Career';
import AdminUsers  from './pages/AdminUsers';
import AdminRoleTypes from './pages/AdminRoleTypes';
import AdminDepartments from './pages/AdminDepartments';
import Profile      from './pages/Profile';
import OrgChart     from './pages/OrgChart';
import KPIs         from './pages/KPIs';
import ReviewCycles from './pages/ReviewCycles';
import ProgramDetails from './pages/ProgramDetails';
import SalesProgram   from './pages/SalesProgram';
import SalesActionPlan from './pages/SalesActionPlan';
import SalesJournal   from './pages/SalesJournal';
import SalesGAS       from './pages/SalesGAS';
import Guide          from './pages/Guide';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F8F7F5' }}>
      <div style={{ width: 32, height: 32, border: '2.5px solid #D3D1C7', borderTopColor: '#1F3B68', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

/**
 * Authorise a registry route against the SAME `roles` list the sidebar filters
 * on (content/modules.js). Nav visibility and route authorisation used to be two
 * independent lists; they drifted, and managers saw three links that bounced
 * them silently home. Reading both from one list is what prevents a recurrence —
 * so guard registry routes with this, never with a hand-written role array.
 *
 * Program gating is deliberately NOT enforced here: it stays a nav-visibility
 * concern, matching today's behaviour where a Tech user can reach a Sales route
 * by URL and the Sales endpoints enforce their program_id = 2 pin server-side.
 */
function RequireModule({ route, children }) {
  const { user } = useAuth();
  const roles = rolesForRoute(route);
  if (!roles) throw new Error(`Route "${route}" is not in content/modules.js — add it or guard it explicitly`);
  if (!roles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <DepartmentProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/reviews/cycles" element={
            <RequireAuth><RequireModule route="/reviews/cycles"><ReviewCycles /></RequireModule></RequireAuth>
          } />
          <Route path="/reviews"  element={<RequireAuth><Reviews /></RequireAuth>} />
          <Route path="/reviews/edit/:userId" element={<RequireAuth><ReviewForm /></RequireAuth>} />
          <Route path="/okrs"     element={<RequireAuth><OKRs /></RequireAuth>} />
          <Route path="/checkins" element={<RequireAuth><Checkins /></RequireAuth>} />
          <Route path="/feedback" element={<RequireAuth><Feedback /></RequireAuth>} />
          <Route path="/career"   element={<RequireAuth><Career /></RequireAuth>} />
          <Route path="/admin/users" element={
            <RequireAuth><RequireModule route="/admin/users"><AdminUsers /></RequireModule></RequireAuth>
          } />
          <Route path="/admin/role-types" element={
            <RequireAuth><RequireModule route="/admin/role-types"><AdminRoleTypes /></RequireModule></RequireAuth>
          } />
          <Route path="/admin/departments" element={
            <RequireAuth><RequireModule route="/admin/departments"><AdminDepartments /></RequireModule></RequireAuth>
          } />
          <Route path="/org"      element={<RequireAuth><OrgChart /></RequireAuth>} />
          <Route path="/guide"    element={<RequireAuth><Guide /></RequireAuth>} />
          <Route path="/evaluations" element={<RequireAuth><KPIs mode="evaluate" /></RequireAuth>} />
          <Route path="/kpi-setup"   element={
            <RequireAuth><RequireModule route="/kpi-setup"><KPIs mode="setup" /></RequireModule></RequireAuth>
          } />
          <Route path="/kpis"        element={<Navigate to="/evaluations" replace />} />
          <Route path="/program"  element={<RequireAuth><ProgramDetails /></RequireAuth>} />
          <Route path="/program/sales" element={<RequireAuth><SalesProgram /></RequireAuth>} />
          <Route path="/sales/action-plans" element={<RequireAuth><SalesActionPlan /></RequireAuth>} />
          <Route path="/sales/journal"      element={<RequireAuth><SalesJournal /></RequireAuth>} />
          <Route path="/sales/gas"          element={<RequireAuth><SalesGAS /></RequireAuth>} />
          <Route path="/profile/:id" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </DepartmentProvider>
    </AuthProvider>
  );
}
